// src/services/homepageService.js
/**
 * Homepage Data Service
 * Provides diverse product feeds for Amazon-style homepage experience
 * 
 * Key Features:
 * - Multi-category product sampling
 * - Controlled randomization
 * - Variety optimization
 * - Data-driven spotlight content
 */

import config from '../config';
import { FetchNestedCategories, FetchProductsBySubCategory } from '../Data';

/**
 * Shuffles array using Fisher-Yates algorithm
 * @param {Array} array - Array to shuffle
 * @returns {Array} - Shuffled copy of array
 */
const shuffleArray = (array) => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

/**
 * Selects N random items from array without duplicates
 * @param {Array} array - Source array
 * @param {number} count - Number of items to sample
 * @returns {Array} - Random sample
 */
const sampleArray = (array, count) => {
  const shuffled = shuffleArray(array);
  return shuffled.slice(0, Math.min(count, array.length));
};

/**
 * Selects diverse subcategories from multiple categories
 * Strategy: Distribute selection across different categories to maximize variety
 * 
 * @param {Array} categories - All available categories
 * @param {number} targetCount - Target number of subcategories to select
 * @returns {Array} - Selected subcategories with metadata
 */
const selectDiverseSubcategories = (categories, targetCount = 5) => {
  const selected = [];
  const categoriesWithSubs = categories.filter(
    cat => cat.subCategories && cat.subCategories.length > 0
  );

  if (categoriesWithSubs.length === 0) return [];

  // Shuffle categories to get variety on each page load
  const shuffledCategories = shuffleArray(categoriesWithSubs);

  // Distribute selection across different categories
  let categoryIndex = 0;
  while (selected.length < targetCount && categoryIndex < shuffledCategories.length) {
    const category = shuffledCategories[categoryIndex];
    
    // Pick 1-2 random subcategories per category for distribution
    const subCatsToTake = Math.min(
      2, // Max 2 subcategories per category
      category.subCategories.length,
      targetCount - selected.length
    );
    
    const sampledSubCats = sampleArray(category.subCategories, subCatsToTake);
    
    // Enrich with parent category info
    sampledSubCats.forEach(subCat => {
      selected.push({
        ...subCat,
        categoryName: category.categoryName,
        categoryId: category.categoryId
      });
    });

    categoryIndex++;
  }

  return selected.slice(0, targetCount);
};

/**
 * Fetches products from multiple subcategories in parallel
 * Uses Promise.all for optimal performance
 * 
 * @param {Array} subcategories - Subcategories to fetch from
 * @param {number} productsPerSubcategory - Max products per subcategory
 * @returns {Promise<Array>} - All products with source metadata
 */
const fetchDiverseProducts = async (subcategories, productsPerSubcategory = 20) => {
  try {
    // Create parallel fetch promises
    const fetchPromises = subcategories.map(subCat =>
      FetchProductsBySubCategory(subCat.subCategoryId)
        .then(products => ({
          subCategory: subCat,
          products: products || []
        }))
        .catch(error => {
          console.error(`Failed to fetch products for ${subCat.subCategoryName}:`, error);
          return { subCategory: subCat, products: [] };
        })
    );

    // Execute all fetches in parallel
    const results = await Promise.all(fetchPromises);
    
    // Flatten all products and add source metadata
    const allProducts = results.flatMap(result => 
      result.products.slice(0, productsPerSubcategory).map(product => ({
        ...product,
        sourceCategory: result.subCategory.categoryName,
        sourceSubCategory: result.subCategory.subCategoryName,
        sourceCategoryId: result.subCategory.categoryId,
        sourceSubCategoryId: result.subCategory.subCategoryId
      }))
    );

    return allProducts;
  } catch (error) {
    console.error('Error fetching diverse products:', error);
    return [];
  }
};

/**
 * Main function: Fetches diverse homepage data
 * 
 * Process:
 * 1. Fetch all categories
 * 2. Select 5 diverse subcategories
 * 3. Fetch products in parallel
 * 4. Create different product sets (trending, best sellers, new arrivals)
 * 
 * @returns {Promise<Object>} - Homepage data with multiple product sets
 */
export const fetchHomepageData = async () => {
  try {
    console.log('🔄 Fetching diverse homepage data...');
    const startTime = performance.now();

    // 1. Fetch all categories
    const categories = await FetchNestedCategories();
    
    if (!categories || categories.length === 0) {
      throw new Error('No categories available');
    }

    console.log(`📂 Found ${categories.length} categories`);

    // 2. Select 5 diverse subcategories
    const selectedSubcategories = selectDiverseSubcategories(categories, 5);
    
    if (selectedSubcategories.length === 0) {
      throw new Error('No subcategories available');
    }

    console.log('🎯 Selected subcategories:', selectedSubcategories.map(sc => 
      `${sc.categoryName} → ${sc.subCategoryName}`
    ));

    // 3. Fetch products from selected subcategories (parallel)
    const allProducts = await fetchDiverseProducts(selectedSubcategories, 20);

    console.log(`📦 Fetched ${allProducts.length} total products`);

    // 4. Create different product sets with different strategies
    
    // Trending: Random shuffle to show variety
    const shuffledProducts = shuffleArray(allProducts);
    const trending = shuffledProducts.slice(0, 12);
    
    // Best sellers: Sort by rating (if available) or use random selection
    const bestSellers = [...allProducts]
      .sort((a, b) => {
        const ratingA = a.rating || a.averageRating || 0;
        const ratingB = b.rating || b.averageRating || 0;
        return ratingB - ratingA;
      })
      .slice(0, 12);
    
    // Ensure best sellers are different from trending
    const trendingIds = new Set(trending.map(p => p.productId));
    const uniqueBestSellers = bestSellers.filter(p => !trendingIds.has(p.productId));
    
    // If we filtered out too many, add more from shuffled
    const finalBestSellers = uniqueBestSellers.length >= 8 
      ? uniqueBestSellers 
      : [
          ...uniqueBestSellers,
          ...shuffledProducts.filter(p => 
            !trendingIds.has(p.productId) && 
            !uniqueBestSellers.find(bs => bs.productId === p.productId)
          )
        ].slice(0, 12);
    
    // New arrivals: Sort by creation date (if available)
    const newArrivals = [...allProducts]
      .sort((a, b) => {
        const dateA = a.createdDate || a.dateAdded ? new Date(a.createdDate || a.dateAdded) : new Date(0);
        const dateB = b.createdDate || b.dateAdded ? new Date(b.createdDate || b.dateAdded) : new Date(0);
        return dateB - dateA;
      })
      .slice(0, 12);

    const endTime = performance.now();
    const loadTime = Math.round(endTime - startTime);

    const metadata = {
      totalProducts: allProducts.length,
      sourcesUsed: selectedSubcategories.length,
      sources: selectedSubcategories.map(sc => ({
        category: sc.categoryName,
        subcategory: sc.subCategoryName,
        categoryId: sc.categoryId,
        subcategoryId: sc.subCategoryId
      })),
      loadTimeMs: loadTime,
      timestamp: new Date().toISOString()
    };

    console.log('✅ Homepage data loaded successfully');
    console.log('📊 Diversity Metrics:', metadata);

    return {
      categories,
      trending,
      bestSellers: finalBestSellers,
      newArrivals,
      allProducts, // Keep for future use
      metadata
    };
  } catch (error) {
    console.error('❌ Error in fetchHomepageData:', error);
    throw error;
  }
};

/**
 * Fetches spotlight content (data-driven)
 * 
 * Strategy:
 * - Rotates featured category based on day of year
 * - Fetches real products from that category
 * - Returns contextual content
 * 
 * Future: Replace with dedicated API endpoint
 * 
 * @returns {Promise<Object>} - Spotlight content object
 */
export const fetchSpotlightContent = async () => {
  try {
    console.log('🎬 Fetching spotlight content...');

    // Future API integration point:
    // const response = await fetch(`${config.baseUrl}/api/Homepage/GetSpotlightCampaign`);
    // return response.data;
    
    const categories = await FetchNestedCategories();
    
    if (!categories || categories.length === 0) {
      return getDefaultSpotlight();
    }

    // Rotate featured category based on day of year (deterministic but changes daily)
    const dayOfYear = Math.floor((new Date() - new Date(new Date().getFullYear(), 0, 0)) / 86400000);
    const featuredCategory = categories[dayOfYear % categories.length];

    // Get a subcategory with products
    const subcategoryWithProducts = featuredCategory.subCategories?.find(
      sc => sc.subCategoryId
    );

    if (!subcategoryWithProducts) {
      return getDefaultSpotlight();
    }

    // Fetch some products from this category for image
    const products = await FetchProductsBySubCategory(subcategoryWithProducts.subCategoryId)
      .catch(() => []);

    // Use first product image or category image
    const imageUrl = products[0]?.imageUrl || 
                     products[0]?.productImages?.[0]?.imageUrl ||
                     featuredCategory.imageUrl || 
                     'https://images.unsplash.com/photo-1607082348824-0a96f2a4b9da?w=600&h=400&fit=crop';

    const spotlight = {
      type: 'category',
      title: `Explore ${featuredCategory.categoryName}`,
      description: `Discover our ${subcategoryWithProducts.subCategoryName.toLowerCase()} collection and more`,
      imageUrl: imageUrl,
      linkTo: `/category/${featuredCategory.categoryId}`,
      categoryName: featuredCategory.categoryName,
      subcategoryName: subcategoryWithProducts.subCategoryName,
      productCount: products.length,
      rotationKey: dayOfYear // For debugging
    };

    console.log('🎬 Spotlight loaded:', spotlight.title);

    return spotlight;
  } catch (error) {
    console.error('❌ Error fetching spotlight content:', error);
    return getDefaultSpotlight();
  }
};

/**
 * Default spotlight fallback content
 * @returns {Object} - Default spotlight data
 */
const getDefaultSpotlight = () => ({
  type: 'campaign',
  title: 'New Arrivals Weekly',
  description: 'Fresh products added every week across all categories',
  imageUrl: 'https://images.unsplash.com/photo-1607082349566-187342175e2f?w=600&h=400&fit=crop',
  linkTo: '/categories'
});

/**
 * Simple in-memory cache for homepage data
 * TTL: 5 minutes (configurable)
 */
class HomepageCache {
  constructor(ttl = 5 * 60 * 1000) { // 5 minutes default
    this.cache = new Map();
    this.ttl = ttl;
  }

  set(key, value) {
    this.cache.set(key, {
      value,
      timestamp: Date.now()
    });
    console.log(`💾 Cached: ${key}`);
  }

  get(key) {
    const item = this.cache.get(key);
    if (!item) return null;
    
    const age = Date.now() - item.timestamp;
    if (age > this.ttl) {
      this.cache.delete(key);
      console.log(`⏰ Cache expired: ${key} (age: ${Math.round(age / 1000)}s)`);
      return null;
    }
    
    console.log(`📦 Cache hit: ${key} (age: ${Math.round(age / 1000)}s)`);
    return item.value;
  }

  clear() {
    this.cache.clear();
    console.log('🗑️ Cache cleared');
  }
}

// Export singleton cache instance
export const homepageCache = new HomepageCache();

/**
 * Cached version of fetchHomepageData
 * @returns {Promise<Object>} - Homepage data (cached or fresh)
 */
export const fetchHomepageDataCached = async () => {
  const cached = homepageCache.get('homepage-data');
  if (cached) {
    return cached;
  }

  const data = await fetchHomepageData();
  homepageCache.set('homepage-data', data);
  
  return data;
};

/**
 * Cached version of fetchSpotlightContent
 * @returns {Promise<Object>} - Spotlight content (cached or fresh)
 */
export const fetchSpotlightContentCached = async () => {
  const cached = homepageCache.get('spotlight-content');
  if (cached) {
    return cached;
  }

  const data = await fetchSpotlightContent();
  homepageCache.set('spotlight-content', data);
  
  return data;
};

/**
 * Fetches all categories with subcategories and product previews
 * For AllCategoriesSection component
 * 
 * Backend Endpoint: GET /api/home/categories-with-subcategories
 * 
 * Response Format:
 * [
 *   {
 *     id: string,
 *     name: string,
 *     slug: string,
 *     subcategories: [
 *       {
 *         id: string,
 *         name: string,
 *         slug: string,
 *         products: [
 *           { id: string, name: string, imageUrl: string }
 *         ]
 *       }
 *     ]
 *   }
 * ]
 * 
 * @param {Array} existingCategories - Optional: Already fetched categories to reuse
 * @returns {Promise<Array>} - Array of categories with nested subcategories and products
 */
export const fetchCategoriesWithSubcategories = async (existingCategories = null) => {
  try {
    console.log('🔄 Fetching categories with subcategories and products...');
    const startTime = performance.now();

    // TODO: Replace with actual backend API call when ready
    // const response = await fetch(`${config.baseUrl}/api/home/categories-with-subcategories`);
    // if (!response.ok) throw new Error(`API Error: ${response.status}`);
    // const data = await response.json();
    // return data;

    // Reuse existing categories if provided, otherwise fetch
    const categories = existingCategories || await FetchNestedCategories();
    
    if (!categories || categories.length === 0) {
      return [];
    }
    
    if (existingCategories) {
      console.log(`📂 Reusing ${categories.length} already-fetched categories...`);
    } else {
      console.log(`📂 Processing ${categories.length} categories...`);
    }

    console.log('🔍 Sample category structure:', {
      firstCategory: categories[0]?.categoryName,
      hasSubCategories: !!categories[0]?.subCategories,
      subCategoryCount: categories[0]?.subCategories?.length || 0,
      firstSubCategory: categories[0]?.subCategories?.[0]?.subCategoryName
    });

    // Transform each category
    const transformedCategories = await Promise.all(
      categories.map(async (category) => {
        // Skip categories without subcategories
        if (!category.subCategories || category.subCategories.length === 0) {
          console.log(`⚠️ Skipping category ${category.categoryName} - no subcategories`);
          return null;
        }

        console.log(`🔄 Processing category: ${category.categoryName} (${category.subCategories.length} subcategories)`);

        // Fetch products for first 4-8 subcategories (limit for performance)
        const subcategoriesWithProducts = await Promise.all(
          category.subCategories.slice(0, 8).map(async (subcategory) => {
            try {
              // Fetch products for this subcategory
              const products = await FetchProductsBySubCategory(subcategory.subCategoryId);
              
              console.log(`  📦 ${subcategory.subCategoryName}: ${products?.length || 0} products`);
              
              // Take only first 4 products (for 2x2 grid)
              const topProducts = (products || []).slice(0, 4).map(product => ({
                id: product.productId || product.id,
                name: product.productName || product.name,
                imageUrl: product.imageUrls[0] || 
                         '/api/placeholder/300/300'
              }));

              // Only include subcategory if it has at least 1 product
              if (topProducts.length === 0) {
                console.log(`  ⚠️ Skipping ${subcategory.subCategoryName} - no products`);
                return null;
              }

              return {
                id: subcategory.subCategoryId,
                name: subcategory.name,
                slug: subcategory.name
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, '-')
                  .replace(/^-|-$/g, ''),
                products: topProducts
              };
            } catch (error) {
              console.error(`  ❌ Failed to fetch products for ${subcategory.subCategoryName}:`, error);
              return null;
            }
          })
        );

        // Filter out null subcategories
        const validSubcategories = subcategoriesWithProducts.filter(sc => sc !== null);

        console.log(`  ✅ Category ${category.categoryName}: ${validSubcategories.length} valid subcategories`);

        // Only include category if it has at least 1 valid subcategory
        if (validSubcategories.length === 0) {
          console.log(`  ⚠️ Skipping category ${category.categoryName} - no valid subcategories`);
          return null;
        }

        return {
          id: category.categoryId,
          name: category.name,
          slug: category.name
            .toLowerCase()
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-|-$/g, ''),
          subcategories: validSubcategories
        };
      })
    );

    // Filter out null categories
    const validCategories = transformedCategories.filter(cat => cat !== null);

    const endTime = performance.now();
    const loadTime = Math.round(endTime - startTime);

    console.log(`✅ Loaded ${validCategories.length} categories with subcategories in ${loadTime}ms`);

    return validCategories;
  } catch (error) {
    console.error('❌ Error fetching categories with subcategories:', error);
    throw error;
  }
};

/**
 * Cached version of fetchCategoriesWithSubcategories
 * @returns {Promise<Array>} - Categories data (cached or fresh)
 */
export const fetchCategoriesWithSubcategoriesCached = async () => {
  const cached = homepageCache.get('categories-with-subcategories');
  if (cached) {
    return cached;
  }

  const data = await fetchCategoriesWithSubcategories();
  homepageCache.set('categories-with-subcategories', data);
  
  return data;
};

// Export all functions
export default {
  fetchHomepageData,
  fetchSpotlightContent,
  fetchHomepageDataCached,
  fetchSpotlightContentCached,
  fetchCategoriesWithSubcategories,
  fetchCategoriesWithSubcategoriesCached,
  homepageCache
};
